import { supabase } from "./supabase";

/**
 * O IO da aba Sugestões (000301 — G9 e G10 do Plano de Melhorias de Produtos).
 *
 * ZERO regra aqui. Quem decide se a sugestão pode ser registrada, se o status
 * pode mudar e se o retorno é obrigatório é o BANCO, e a recusa dele vem com a frase pronta (`detalhe`). Traduzir
 * `motivo` aqui criaria uma segunda tabela de frases, e a que divergisse em
 * silêncio seria a que ninguém lê — a mesma regra de `lib/adminCatalogo.ts`.
 *
 * Nenhum `select` cru: as três tabelas têm `revoke all`, e a leitura sai por
 * função com o portão dentro (a cicatriz da 000136).
 */

export type StatusDaSugestao = "nova" | "em_andamento" | "atendida" | "nao_atendida";
export type Prioridade = "baixa" | "media" | "alta";
/** O Fórum entrou em 28/09 (000321). */
export type Origem = "area_aluno" | "forum" | "whatsapp" | "email" | "pesquisa" | "formulario" | "suporte" | "outro";

export const ROTULO_DO_STATUS: Record<StatusDaSugestao, string> = {
  nova: "Nova",
  em_andamento: "Em andamento",
  atendida: "Atendida",
  nao_atendida: "Não atendida",
};

export const ROTULO_DA_PRIORIDADE: Record<Prioridade, string> = {
  alta: "Alta",
  media: "Média",
  baixa: "Baixa",
};

export const ROTULO_DA_ORIGEM: Record<Origem, string> = {
  area_aluno: "Área do aluno",
  forum: "Fórum",
  whatsapp: "WhatsApp",
  email: "E-mail",
  pesquisa: "Pesquisa",
  formulario: "Formulário",
  suporte: "Suporte",
  outro: "Outro",
};

export type Sugestao = {
  id: string;
  numero: number;
  titulo: string;
  descricao: string | null;
  curso_slug: string | null;
  curso_nome: string | null;
  categoria_id: string;
  categoria: string;
  prioridade: Prioridade;
  origem: Origem;
  status: StatusDaSugestao;
  /** O que Produtos responde — é o que o CS diz ao aluno. */
  retorno: string | null;
  registrada_por: string | null;
  registrada_em: string;
  atualizada_em: string;
  /** A tarefa do ClickUp de onde ela veio (000319); nula = registrada no hub. */
  clickup_id: string | null;
  /** O endereço da tarefa, DERIVADO no banco — a tela não monta link de sistema de fora. */
  clickup_url: string | null;
};

/** Uma sugestão que um master excluiu (000319) — o registro fica, e a tela o mostra. */
export type SugestaoExcluida = {
  id: number;
  numero: number;
  titulo: string;
  motivo: string;
  por: string;
  em: string;
  clickup_id: string | null;
};

/** Um comentário da triagem ANTIGA, trazido do ClickUp na importação (000319). */
export type ComentarioDoClickUp = {
  autor: string;
  em: string | null;
  texto: string;
  respostas?: ComentarioDoClickUp[];
};

/** O que só o ClickUp sabia: vai no `detalhe` do evento `importou`. */
export type ProcedenciaDoClickUp = {
  id?: string;
  url?: string;
  status?: string | null;
  responsaveis?: string[];
  atendente?: string | null;
  prints?: number;
  descricao_da_tarefa?: string | null;
  conversa?: ComentarioDoClickUp[];
  trechos_retirados?: number;
};

export type CategoriaDeSugestao = {
  id: string;
  rotulo: string;
  ativo: boolean;
  ordem: number;
  /** Quantas sugestões usam esta categoria — é por isso que ela desliga e não se apaga. */
  sugestoes: number;
};

export type ListaDeSugestoes = {
  ok: true;
  total: number;
  /** A contagem por status sobre os OUTROS filtros — é o que os chips mostram. */
  contagem: Record<StatusDaSugestao, number>;
  itens: Sugestao[];
  categorias: CategoriaDeSugestao[];
  cursos: { slug: string; nome: string }[];
  /** As excluídas mais recentes (até 50), e o total ao lado. */
  excluidas: SugestaoExcluida[];
  excluidas_total: number;
};

/** A resposta de todo ato. `detalhe` é a frase do BANCO — a tela não a reescreve. */
export type Resposta = {
  ok: boolean;
  motivo?: string;
  detalhe?: string;
  [extra: string]: unknown;
};

export type EventoDaSugestao = {
  id: number;
  acao: "registrou" | "mudou_status" | "editou" | "importou";
  de_status: StatusDaSugestao | null;
  para_status: StatusDaSugestao | null;
  detalhe: Record<string, unknown> | null;
  motivo: string | null;
  por: string;
  em: string;
};

export async function lerSugestoes(filtros: {
  status?: StatusDaSugestao | null;
  /** `-` = sem curso. */
  curso?: string | null;
  categoria?: string | null;
  /** 000321: os dois entram no mesmo recorte da contagem por status. */
  prioridade?: Prioridade | null;
  origem?: Origem | null;
  busca?: string | null;
  limite?: number;
  offset?: number;
}): Promise<ListaDeSugestoes | Resposta> {
  const { data, error } = await supabase.rpc("cs_sugestoes_listar", {
    p_status: filtros.status ?? null,
    p_curso: filtros.curso || null,
    p_categoria: filtros.categoria || null,
    p_busca: filtros.busca?.trim() || null,
    p_limite: filtros.limite ?? 50,
    p_offset: filtros.offset ?? 0,
    p_prioridade: filtros.prioridade || null,
    p_origem: filtros.origem || null,
  });
  if (error) return { ok: false, detalhe: error.message };
  return data as ListaDeSugestoes | Resposta;
}

export async function registrarSugestao(campos: {
  titulo: string;
  descricao: string;
  curso: string;
  categoria: string;
  prioridade: Prioridade;
  origem: Origem;
}): Promise<Resposta> {
  const { data, error } = await supabase.rpc("cs_sugestao_registrar", {
    p_titulo: campos.titulo,
    p_descricao: campos.descricao,
    p_curso_slug: campos.curso || null,
    p_categoria: campos.categoria || null,
    p_prioridade: campos.prioridade,
    p_origem: campos.origem,
  });
  if (error) return { ok: false, detalhe: error.message };
  return data as Resposta;
}

export async function mudarStatusDaSugestao(
  id: string,
  status: StatusDaSugestao,
  retorno?: string | null,
  motivo?: string | null,
): Promise<Resposta> {
  const { data, error } = await supabase.rpc("cs_sugestao_mudar_status", {
    p_id: id,
    p_status: status,
    p_retorno: retorno || null,
    p_motivo: motivo || null,
  });
  if (error) return { ok: false, detalhe: error.message };
  return data as Resposta;
}

/** Nulo não mexe; vazio limpa — o contrato das RPCs desta casa. Curso vazio = sem curso. */
export async function editarSugestao(
  id: string,
  campos: {
    titulo?: string | null;
    descricao?: string | null;
    curso?: string | null;
    categoria?: string | null;
    prioridade?: Prioridade | null;
    origem?: Origem | null;
    motivo?: string | null;
  },
): Promise<Resposta> {
  const { data, error } = await supabase.rpc("cs_sugestao_editar", {
    p_id: id,
    p_titulo: campos.titulo ?? null,
    p_descricao: campos.descricao ?? null,
    p_curso_slug: campos.curso ?? null,
    p_categoria: campos.categoria || null,
    p_prioridade: campos.prioridade ?? null,
    p_origem: campos.origem ?? null,
    p_motivo: campos.motivo || null,
  });
  if (error) return { ok: false, detalhe: error.message };
  return data as Resposta;
}

export async function lerHistoricoDaSugestao(id: string): Promise<Resposta & { eventos?: EventoDaSugestao[] }> {
  const { data, error } = await supabase.rpc("cs_sugestao_historico", { p_id: id });
  if (error) return { ok: false, detalhe: error.message };
  return data as Resposta & { eventos?: EventoDaSugestao[] };
}

/** As categorias são de PRODUTOS: salvar pede editar na aba Catálogo, e o banco diz isso na recusa. */
export async function salvarCategoriaDeSugestao(campos: {
  id?: string | null;
  rotulo?: string | null;
  ativo?: boolean | null;
  ordem?: number | null;
}): Promise<Resposta> {
  const { data, error } = await supabase.rpc("cs_categoria_salvar", {
    p_id: campos.id ?? null,
    p_rotulo: campos.rotulo ?? null,
    p_ativo: campos.ativo ?? null,
    p_ordem: campos.ordem ?? null,
  });
  if (error) return { ok: false, detalhe: error.message };
  return data as Resposta;
}

/**
 * Exclui a sugestão — só o MASTER, com motivo (000319). Desde a 000333 não há
 * mais cópia na Central a tirar antes: o espelho saiu, e a exclusão é a RPC
 * direta, com os dois parâmetros.
 */
export async function excluirSugestao(id: string, motivo: string): Promise<Resposta> {
  const { data, error } = await supabase.rpc("cs_sugestao_excluir", {
    p_id: id,
    p_motivo: motivo || null,
  });
  if (error) return { ok: false, detalhe: error.message };
  return data as Resposta;
}

/** A frase de reserva, para quando nem a RPC nem a rede responderam. */
export function mensagemDaSugestao(r: Resposta | null, erro?: string): string {
  if (erro) return erro;
  if (!r) return "não foi possível falar com o servidor";
  if (r.detalhe) return r.detalhe;
  return r.ok ? "feito" : "não foi possível salvar";
}
